/** A record spine's width rule (plan §1.1, §1.6; DESIGN.md §5, the Zero-Re-Layout Rule):
 *  the layout follows the width the panels have in EDIT mode, in both modes, so entering
 *  edit mode never re-lays anything out. From 1100 px of content the ledger docks beside
 *  the panels (`LedgerDock`) and takes its column out of that width; two panel columns
 *  from 1200 px of content, else one, in lifecycle order (a 1440 px laptop with the nav
 *  drawer open: one column beside the docked ledger; with the rail collapsed: two). */
import { LEDGER_GAP_PX, LEDGER_WIDTH, SHEET_BELOW_PX } from '@/features/records/ledger/LedgerDock';

/** Between the panel columns and between panels. */
export const PANEL_GAP_PX = 16;
/** Two panel columns from this much page content (plan §1.1, §1.6: one column below
 *  ~1200 px) — decided on the width the panels have in edit mode. */
export const TWO_PANEL_COLUMNS_FROM_PX = 1200;
/** The narrowest a panel gets in the two-column masonry: what 1200 px of content leaves
 *  each of two panels beside the docked ledger (414 px) — one field column, with room for
 *  a party row's name and its pre-check-in control. */
export const PANEL_MIN_PX = (TWO_PANEL_COLUMNS_FROM_PX - LEDGER_WIDTH - LEDGER_GAP_PX - PANEL_GAP_PX) / 2;

export interface SpineColumns {
  /** The ledger docks beside the panels in edit mode (else: the bottom sheet). */
  docked: boolean;
  /** The width the panels have in edit mode — what the layout follows in both modes. */
  editWidth: number;
  columns: 1 | 2;
}

/** The spine's layout for `width` px of page content (ledger column included); 0 =
 *  unmeasured: docked, two columns, unpinned field grids. */
export function spineColumns(width: number): SpineColumns {
  if (width === 0) return { docked: true, editWidth: 0, columns: 2 };
  const docked = width >= SHEET_BELOW_PX;
  const editWidth = docked ? width - LEDGER_WIDTH - LEDGER_GAP_PX : width;
  return { docked, editWidth, columns: editWidth >= 2 * PANEL_MIN_PX + PANEL_GAP_PX ? 2 : 1 };
}
